/**
 * ATL-1445 stage 3, P0 — the `write-bundle-coverage` gate clause's evidence.
 *
 * Event: `write_w_capability_reported`, once per (JS process, organization), from `AppStack` as soon
 * as the signed-in organization is known. It says, for the bundle that is RUNNING, whether it can
 * honour a write cutover (`global/Write/writeWCapability.ts`) and exactly which bundle that is.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY AN EVENT AND NOT A SUPER PROPERTY
 *
 * The obvious design — register `write_w_capable` next to `update_id` in `buildProvenanceProperties`
 * so every event carries it — is wrong in a way that would eventually make the gate pass a device
 * that must block. PostHog PERSISTS super properties to disk, and a bundle only overwrites the keys
 * it knows. A device that ran a capable bundle and then launches an OLDER one (the embedded bundle
 * after an OTA fails to load, an EAS rollback, a store binary reinstalled) re-registers its own
 * `update_id` but leaves the newer bundle's `write_w_capable: true` in place, because the old bundle
 * has never heard of that key. Its events would then say "old bundle, capable" — the exact false GO
 * the clause exists to prevent.
 *
 * An event has no such memory. Every property below is computed by the running process at emit time
 * and passed explicitly (event properties override super properties in posthog-core's
 * `enrichProperties`), so `update_id` / `web_build_at` and `write_w_capable` always describe the SAME
 * bundle. A bundle older than this file emits no such event at all, which the gate reads as
 * `unverifiable` — never as capable.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * HOW THE GATE IS MEANT TO READ IT (contract: atlas-api-v1-write-cutover.md §5, "Telemetry contract")
 *
 * Capability is a property of the BUNDLE, so the gate derives a table bundle-key → capability from
 * these events (bundle key = platform + `update_id` on native, platform + `web_build_at` on web —
 * the same keys `ota-coverage` classifies by), then requires every device row of the organization's
 * 14-day `ota-coverage` rollup to map to a key reported capable at or above the required
 * `write_w_seam_version`. No report for a key ⇒ `unverifiable` (blocks). This is why the event does
 * not need to repeat itself on a till that stays open for weeks: one report per bundle is enough.
 *
 * The bundle's publish instant is included pre-decoded (`bundle_published_at`, from the EAS update
 * id's UUIDv7 prefix or the web build stamp, via `resolveRunningBundleInstant` — the same decoding
 * `ota-coverage.util.ts` uses) for readability only; the gate must decode `update_id` itself, as it
 * already does, rather than trust a client-side derivation.
 *
 * No PII: booleans, versions, bundle identifiers and the organization's Firestore id (the PostHog
 * `organization` group key — opaque, not personal).
 */
import { captureOperationalEvent, type OperationalProperties } from './operationalTelemetry'
import { WRITE_W_CAPABILITY, type WriteWCapability } from '../Write/writeWCapability'
import { resolveRunningBundleInstant } from '../../utils/bundleIdentity'
import { buildProvenanceProperties } from '../../utils/buildProvenance'
import { FIRESTORE_ONLY_WRITER_REGISTRY_VERSION } from './firestoreOnlyWriterRegistry'

export const WRITE_W_CAPABILITY_EVENT = 'write_w_capability_reported'

const stringOrNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const booleanOrNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null)

/**
 * The running bundle's identity, in the property names the `ota-coverage` rollup already reads.
 *
 * Built FROM `buildProvenanceProperties()` — the payload the gate has read since ATL-531/ATL-895 —
 * rather than re-reading `expo-updates` here, so the two can never describe a bundle differently
 * (and the web build stamp keeps its one literal `process.env.EXPO_PUBLIC_*` read, which is what the
 * bundler inlines). Values are narrowed to what an operational event may carry; anything else becomes
 * null, which the gate reads as unverifiable, never as a match.
 */
export const runningBundleProperties = (): OperationalProperties => {
  const p: Record<string, unknown> = buildProvenanceProperties()
  const platform = stringOrNull(p.platform)
  const updateId = stringOrNull(p.update_id)
  const updateIsEmbedded = booleanOrNull(p.update_is_embedded)
  const webBuildAt = stringOrNull(p.web_build_at)
  const identity = resolveRunningBundleInstant({
    platform: platform ?? '',
    updateId,
    isEmbeddedLaunch: updateIsEmbedded,
    webBuildAt,
  })
  return {
    platform,
    update_id: updateId,
    update_is_embedded: updateIsEmbedded,
    updates_enabled: booleanOrNull(p.updates_enabled),
    runtime_version: stringOrNull(p.runtime_version),
    update_channel: stringOrNull(p.update_channel),
    js_version: stringOrNull(p.js_version),
    web_build_at: webBuildAt,
    bundle_published_at: identity.at ? identity.at.toISOString() : null,
    bundle_identity_source: identity.source,
  }
}

export const buildWriteBundleCapabilityProperties = (
  organizationId: string,
  capability: WriteWCapability = WRITE_W_CAPABILITY,
): OperationalProperties => ({
  write_w_capable: capability.capable,
  write_w_seam_version: capability.seamVersion,
  write_w_reason: capability.reason,
  write_w_manifest: capability.manifest,
  // The Firestore-only writer registry this bundle carries. The `firestore-only-writer-usage` clause
  // reads a writer's ABSENCE as "unused" only from bundles at or above that writer's
  // `addedInRegistryVersion` — a bundle that predates a writer cannot emit it. This event is the
  // per-device statement of that version, present even on a device that never used any writer.
  firestore_only_writer_registry_version: FIRESTORE_ONLY_WRITER_REGISTRY_VERSION,
  organization_id: organizationId,
  ...runningBundleProperties(),
})

/** Organizations already reported by this JS process. A reload (OTA, cutover) starts a new one. */
const reportedOrganizations = new Set<string>()

/**
 * Report this bundle's write-cutover capability for `organizationId`, once per JS process.
 * Never throws: telemetry must not be able to break the app start it rides on.
 *
 * The organization is marked reported only AFTER the event was handed to `captureOperationalEvent`.
 * If building the properties throws (a native module answering unexpectedly), nothing is marked, so
 * the next trigger — AppStack's effect runs again only when the organization changes, or on the next
 * launch — may try again. That is at most one attempt per trigger, never a loop; and after one
 * hand-off, never again in this process.
 *
 * "Handed to captureOperationalEvent" is not "delivered to PostHog". If the sink is not registered
 * yet the event waits in that module's buffer (20 events); if more than 20 queue before registration
 * the OLDEST are dropped and counted as `operational_telemetry_buffer_overflowed`. A capability report
 * lost that way is not re-sent in this process — the next launch reports again, and the gate reads a
 * bundle with no report as `unverifiable`, never as capable, so the loss can only delay a GO.
 *
 * @returns whether an event was captured by this call.
 */
export const reportWriteBundleCapability = (organizationId: unknown): boolean => {
  try {
    if (typeof organizationId !== 'string' || organizationId === '') return false
    if (reportedOrganizations.has(organizationId)) return false
    const properties = buildWriteBundleCapabilityProperties(organizationId)
    captureOperationalEvent(WRITE_W_CAPABILITY_EVENT, properties)
    reportedOrganizations.add(organizationId)
    return true
  } catch {
    return false
  }
}

/** Test seam. */
export const resetWriteBundleCapabilityForTests = (): void => {
  reportedOrganizations.clear()
}
